/**
 * GDPR analytics-consent barrier (Kapitalvins parity).
 *
 * When consent is REQUIRED (opt-in via `requireConsent`), the SDK must not
 * initialise — and therefore emit nothing, create no persistent id — until the
 * visitor has granted analytics consent. Consent is read from a cookie or a
 * `localStorage` key written by the site's consent banner; this module assumes
 * nothing about the banner beyond the stored value.
 *
 * Default (`requireConsent` false): always granted — telemetry is driven solely by
 * the operator's config, preserving the historical behaviour. Leaf module (no SDK
 * imports); reads/writes are guarded — it never throws.
 */

/** Default storage/cookie key the banner is expected to write. */
const DEFAULT_KEY = 'observ.consent'
/** Values that count as "granted" (case-insensitive). */
const DEFAULT_GRANTED = ['granted', 'true', '1', 'yes', 'all']

/** The subset of init options this module reads (ObservInitOptions satisfies it). */
export interface ConsentOptions {
  requireConsent?: boolean
  consentKey?: string
  consentGrantedValues?: string[]
}

function readCookie(key: string): string | undefined {
  if (typeof document === 'undefined' || !document.cookie) return undefined
  for (const part of document.cookie.split(';')) {
    const idx = part.indexOf('=')
    if (idx === -1) continue
    if (part.slice(0, idx).trim() === key) {
      try {
        return decodeURIComponent(part.slice(idx + 1).trim())
      } catch {
        return part.slice(idx + 1).trim()
      }
    }
  }
  return undefined
}

function readStorage(key: string): string | undefined {
  try {
    return globalThis.localStorage?.getItem(key) ?? undefined
  } catch {
    // localStorage can throw (private mode, cookies blocked) — treat as absent.
    return undefined
  }
}

/** The effective consent key for these options. */
export function consentKey(opts: ConsentOptions): string {
  return opts.consentKey || DEFAULT_KEY
}

/** The value {@link writeConsent} persists when granting programmatically. */
function grantedValue(opts: ConsentOptions): string {
  return opts.consentGrantedValues?.[0] ?? 'granted'
}

/**
 * True when the SDK is allowed to start. With `requireConsent` off, always true;
 * otherwise the configured cookie is checked first, then `localStorage`.
 */
export function hasAnalyticsConsent(opts: ConsentOptions): boolean {
  if (!opts.requireConsent) return true
  const key = consentKey(opts)
  const granted = (opts.consentGrantedValues ?? DEFAULT_GRANTED).map((v) => v.toLowerCase())
  const value = readCookie(key) ?? readStorage(key)
  return value != null && granted.includes(value.toLowerCase())
}

/** Persist analytics consent to `localStorage` (used by `observ.grantConsent()`). */
export function writeConsent(opts: ConsentOptions): void {
  try {
    globalThis.localStorage?.setItem(consentKey(opts), grantedValue(opts))
  } catch {
    /* storage blocked — the in-tab grant still starts the SDK */
  }
}

/** Remove the stored consent so future inits gate again (used by `revokeConsent()`). */
export function clearConsent(opts: ConsentOptions): void {
  try {
    globalThis.localStorage?.removeItem(consentKey(opts))
  } catch {
    /* swallow */
  }
}
